import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useUniversitySession } from '@/hooks/useUniversitySession'
import QuizInterface from '@/components/education/QuizInterface'
import ShortAnswerBox from '@/components/university/ShortAnswerBox'
import EssayDropbox from '@/components/university/EssayDropbox'
import LanguagePicker from '@/components/ui/LanguagePicker'
import SEO from '@/components/common/SEO'

// ─── Design Tokens ──────────────────────────────────────────────────────────
const M = '#06060c'
const G = '#d4a843'
const IN = '#0ea5e9'
const BR = 'rgba(255,255,255,0.07)'
const CA = 'rgba(255,255,255,0.05)'
const TX = 'rgba(255,255,255,0.88)'
const MU = 'rgba(255,255,255,0.45)'

// ─── Types ──────────────────────────────────────────────────────────────────
interface LessonMeta {
  lesson_id: number
  title: string
  learning_objective: string
  core_concepts: string[]
}
interface ModuleMeta {
  module_id: number
  title: string
  lessons: LessonMeta[]
}
type EssayMode = 'none' | 'short_answer_only' | 'essay_per_module' | 'capstone_only'
interface ClassInfo {
  id: string
  name: string
  module_plan: ModuleMeta[]
  essay_mode: EssayMode
}

// ─── Utility: strip EDU_UPDATE fragments from display text ──────────────────
// The AI appends EDU_UPDATE::{...} at the end of every message.
// The edge function strips it server-side, but during streaming partial
// fragments like "EDU" or "EDU_UPDATE" can leak through.
function cleanEduContent(raw: string): string {
  return raw
    .replace(/EDU_UPDATE::\{[^}]*\}/g, '')   // full EDU_UPDATE::{...}
    .replace(/EDU_UPDATE::/g, '')              // EDU_UPDATE:: alone
    .replace(/EDU_UPDATE/g, '')                // bare EDU_UPDATE
    .replace(/\bEDU\b/g, '')                   // leaked partial "EDU" fragment
    .replace(/\n{3,}/g, '\n\n')               // collapse triple+ newlines
    .trimEnd()
}

// ─── Markdown Components (dark-theme styled for university) ─────────────────
const markdownComponents = {
  p: ({ children, ...props }: any) => (
    <p {...props} style={{
      fontSize: 16, lineHeight: 1.75, marginBottom: '1em', color: TX,
      fontWeight: 450, letterSpacing: '0.01em'
    }}>{children}</p>
  ),
  strong: ({ children, ...props }: any) => (
    <strong {...props} style={{ fontWeight: 700, color: '#fff', textShadow: '0 0 1px rgba(255,255,255,0.1)' }}>{children}</strong>
  ),
  em: ({ children, ...props }: any) => (
    <em {...props} style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)' }}>{children}</em>
  ),
  h1: ({ children, ...props }: any) => (
    <h1 {...props} style={{
      fontSize: 24, fontWeight: 800, color: G, marginBottom: '0.6em', marginTop: '1em',
      letterSpacing: '-0.3px', lineHeight: 1.25, borderBottom: `1px solid ${BR}`, paddingBottom: '0.3em'
    }}>{children}</h1>
  ),
  h2: ({ children, ...props }: any) => (
    <h2 {...props} style={{
      fontSize: 20, fontWeight: 700, color: '#f0e6cf', marginBottom: '0.5em', marginTop: '0.9em',
      lineHeight: 1.3,
    }}>{children}</h2>
  ),
  h3: ({ children, ...props }: any) => (
    <h3 {...props} style={{
      fontSize: 17, fontWeight: 700, color: 'rgba(255,255,255,0.9)', marginBottom: '0.4em',
      marginTop: '0.8em', lineHeight: 1.4,
    }}>{children}</h3>
  ),
  ul: ({ children, ...props }: any) => (
    <ul {...props} style={{
      paddingLeft: '1.4em', marginBottom: '1em', listStyleType: 'none',
    }}>{children}</ul>
  ),
  ol: ({ children, ...props }: any) => (
    <ol {...props} style={{
      paddingLeft: '1.4em', marginBottom: '1em', listStyleType: 'decimal', color: MU
    }}>{children}</ol>
  ),
  li: ({ children, ...props }: any) => (
    <li {...props} style={{
      position: 'relative', fontSize: 16, lineHeight: 1.7, color: TX, marginBottom: '0.5em',
    }}>
      {props.ordered ? null : <span style={{ position: 'absolute', left: '-1.1em', color: G }}>•</span>}
      {children}
    </li>
  ),
  blockquote: ({ children, ...props }: any) => (
    <blockquote {...props} style={{
      borderLeft: `4px solid ${G}`, padding: '16px 20px', margin: '1.5em 0',
      background: 'rgba(212,168,67,0.05)', borderRadius: '0 12px 12px 0',
      fontStyle: 'italic', color: 'rgba(255,255,255,0.7)',
    }}>{children}</blockquote>
  ),
  code: ({ children, className, ...props }: any) => {
    const isInline = !className
    return isInline ? (
      <code {...props} style={{
        background: 'rgba(212,168,67,0.15)', color: '#f5e0a0',
        padding: '2px 6px', borderRadius: 4, fontSize: '0.925em',
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        border: '1px solid rgba(212,168,67,0.2)'
      }}>{children}</code>
    ) : (
      <code {...props} style={{
        display: 'block', background: 'rgba(0,0,0,0.4)', color: '#e2dcca',
        padding: 20, borderRadius: 14, fontSize: 14, overflowX: 'auto',
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        border: `1px solid ${BR}`, margin: '1.2em 0',
        lineHeight: 1.5
      }}>{children}</code>
    )
  },
  a: ({ children, ...props }: any) => (
    <a {...props} style={{ color: IN, textDecoration: 'none', borderBottom: `1px solid ${IN}`, paddingBottom: 1 }}
      target="_blank" rel="noreferrer">{children}</a>
  ),
  hr: (props: any) => (
    <hr {...props} style={{
      border: 'none', borderTop: `1px solid ${BR}`,
      margin: '2em 0',
    }} />
  ),
  table: ({ children, ...props }: any) => (
    <div style={{ overflowX: 'auto', marginBottom: '1.2em', borderRadius: 12, border: `1px solid ${BR}` }}>
      <table {...props} style={{
        borderCollapse: 'collapse', width: '100%', fontSize: 14, background: 'rgba(255,255,255,0.02)'
      }}>{children}</table>
    </div>
  ),
  th: ({ children, ...props }: any) => (
    <th {...props} style={{
      padding: '12px 16px', textAlign: 'left', fontWeight: 700,
      background: 'rgba(255,255,255,0.05)',
      borderBottom: `2px solid ${BR}`, color: G,
    }}>{children}</th>
  ),
  td: ({ children, ...props }: any) => (
    <td {...props} style={{
      padding: '10px 16px', borderBottom: `1px solid ${BR}`,
      color: TX,
    }}>{children}</td>
  ),
}

// ─── Typing Indicator ───────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div style={{ display: 'flex', gap: 5, padding: '16px 20px', alignItems: 'center' }}>
      {[0, 1, 2].map(i => (
        <motion.div key={i} animate={{ y: [0, -5, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
          style={{ width: 7, height: 7, borderRadius: '50%', background: G }} />
      ))}
    </div>
  )
}

// ─── Message Bubble ─────────────────────────────────────────────────────────
function MessageBubble({ content, role, isMobile }: {
  content: string; role: 'user' | 'assistant'; isMobile: boolean
}) {
  const cleaned = role === 'assistant' ? cleanEduContent(content) : content

  return (
    <div style={{
      display: 'flex',
      justifyContent: role === 'user' ? 'flex-end' : 'flex-start',
      marginBottom: 20,
      gap: 10,
      alignItems: 'flex-start',
    }}>
      {role === 'assistant' && (
        <div style={{
          width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
          background: `linear-gradient(135deg, ${IN}, ${G})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, marginTop: 4,
          boxShadow: '0 4px 12px rgba(14,165,233,0.25)',
        }}>🎓</div>
      )}

      <div style={{
        maxWidth: isMobile ? (role === 'user' ? '85%' : '82%') : '78%',
        padding: isMobile ? '13px 16px' : '15px 19px',
        borderRadius: role === 'user' ? '18px 18px 4px 18px' : '4px 18px 18px 18px',
        background: role === 'user'
          ? `linear-gradient(135deg, ${IN}, #0284c7)`
          : CA,
        border: role === 'user' ? 'none' : `1px solid ${BR}`,
        boxShadow: role === 'user'
          ? '0 4px 14px rgba(14,165,233,0.22)'
          : '0 2px 10px rgba(0,0,0,0.12)',
      }}>
        {role === 'assistant' ? (
          <div className="prose-university">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {cleaned}
            </ReactMarkdown>
          </div>
        ) : (
          <p style={{
            fontSize: 15, lineHeight: 1.6, color: '#fff',
            whiteSpace: 'pre-wrap', margin: 0,
          }}>{cleaned}</p>
        )}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── SessionInner ────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function SessionInner({ classId, classInfo, moduleId, lessonId }: {
  classId: string
  classInfo: ClassInfo
  moduleId: number
  lessonId: number
}) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const mod = classInfo.module_plan.find(m => m.module_id === moduleId)
  const lesson = mod?.lessons.find(l => l.lesson_id === lessonId)

  // Determine assessment type
  const essayMode = classInfo.essay_mode || 'none'
  const totalModules = classInfo.module_plan.length
  const totalLessons = mod?.lessons.length ?? 0
  const isLastLesson = lessonId === totalLessons
  const isLastModule = moduleId === totalModules
  const isCapstoneLesson = isLastLesson && isLastModule

  type AssessmentKind = 'short_answer' | 'essay' | 'none'
  let assessmentKind: AssessmentKind = 'none'
  if (essayMode === 'short_answer_only') {
    assessmentKind = 'short_answer'
  } else if (essayMode === 'essay_per_module' && isLastLesson) {
    assessmentKind = 'essay'
  } else if (essayMode === 'capstone_only' && isCapstoneLesson) {
    assessmentKind = 'essay'
  }

  const {
    messages, isLoading, isStreaming, error,
    sessionComplete, suggestedChips,
    quiz, quizOpen, quizLoading, quizResult,
    completedLessons,
    sendMessage, clearError, submitQuiz, dismissQuiz, updateEnrollmentProgress
  } = useUniversitySession(
    classId,
    moduleId,
    lessonId,
    lesson?.title || '',
    lesson?.learning_objective || '',
    lesson?.core_concepts || [],
  )

  // Helper: check if a lesson is completed based on DB state
  function isLessonDone(mId: number, lId: number): boolean {
    return !!completedLessons[`${mId}-${lId}`]
  }

  const [input, setInput] = useState('')
  const [isMobile, setIsMobile] = useState(false)
  const [showSidebar, setShowSidebar] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth <= 820
      setIsMobile(mobile)
      if (!mobile) setShowSidebar(true)
    }
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isLoading])

  function handleSend() {
    const text = input.trim()
    if (!text || isLoading || isStreaming) return
    setInput('')
    sendMessage(text)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  function goNextLesson() {
    if (!mod) return
    const next = mod.lessons.find(l => l.lesson_id === lessonId + 1)
    if (next) {
      navigate(`/learn/${classId}?module=${moduleId}&lesson=${next.lesson_id}`)
    } else {
      const nextMod = classInfo.module_plan.find(m => m.module_id === moduleId + 1)
      nextMod
        ? navigate(`/learn/${classId}?module=${nextMod.module_id}&lesson=1`)
        : navigate(`/degree/${classId}`)
    }
  }

  return (
    <div style={{ display: 'flex', height: '100dvh', background: M, overflow: 'hidden', fontFamily: "'Inter', sans-serif" }}>
      <SEO title={lesson?.title || classInfo.name} noindex />

      {/* ── Global prose styles for markdown inside assistant bubbles ── */}
      <style>{`
        .prose-university p:last-child { margin-bottom: 0; }
        .prose-university ul, .prose-university ol { margin-top: 0.25em; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Sidebar Overlay for Mobile */}
      <AnimatePresence>
        {isMobile && showSidebar && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSidebar(false)}
            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, backdropFilter: 'blur(4px)' }}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div
        animate={{
          x: isMobile ? (showSidebar ? 0 : -280) : 0,
          width: isMobile ? 280 : 260
        }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        style={{
          position: isMobile ? 'absolute' : 'relative',
          height: '100%',
          flexShrink: 0,
          borderRight: `1px solid ${BR}`,
          background: isMobile ? '#121225' : 'rgba(255,255,255,0.02)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 101
        }}
      >
        <div style={{ padding: '24px 20px 16px', borderBottom: `1px solid ${BR}` }}>
          <button onClick={() => navigate(`/degree/${classId}`)} style={{ background: 'none', border: 'none', color: MU, cursor: 'pointer', fontSize: 11, padding: 0, marginBottom: 12, fontWeight: 700, letterSpacing: '0.5px' }}>
            ← {t('university.session.backToDegree', 'DEGREE OVERVIEW')}
          </button>
          <p style={{ fontSize: 11, color: G, fontWeight: 800, letterSpacing: '1.5px', marginBottom: 6, textTransform: 'uppercase' }}>
            {t('university.session.lesson', 'Lesson {{n}}', { n: lessonId })}
          </p>
          <p style={{ fontSize: 15, fontWeight: 700, color: TX, lineHeight: 1.3 }}>{lesson?.title}</p>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 10px' }} className="scrollbar-hide">
          {/* Progress Widget */}
          <div style={{ background: 'rgba(212,168,67,0.06)', borderRadius: 14, padding: 12, marginBottom: 20, border: '1px solid rgba(212,168,67,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: G, letterSpacing: 0.5 }}>PROGRESS</span>
              <span style={{ fontSize: 10, fontWeight: 800, color: G }}>{Math.round((Object.keys(completedLessons).length / (classInfo.module_plan.reduce((a, b) => a + (b.lessons?.length || 0), 0) || 1)) * 100)}%</span>
            </div>
            <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
              <motion.div initial={{ width: 0 }} animate={{ width: `${(Object.keys(completedLessons).length / (classInfo.module_plan.reduce((a, b) => a + (b.lessons?.length || 0), 0) || 1)) * 100}%` }} style={{ height: '100%', background: G }} />
            </div>
            {lesson?.core_concepts && lesson.core_concepts.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <p style={{ fontSize: 9, fontWeight: 800, color: MU, marginBottom: 6, letterSpacing: 1 }}>CORE CONCEPTS</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {lesson.core_concepts.slice(0, 4).map(c => (
                    <span key={c} style={{ fontSize: 9, background: 'rgba(255,255,255,0.04)', color: TX, padding: '3px 6px', borderRadius: 4, border: `1px solid ${BR}` }}>{c}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {classInfo.module_plan.map(m => (
            <div key={m.module_id} style={{ marginBottom: 6 }}>
              <div style={{ padding: '8px 10px', fontSize: 11, fontWeight: 800, color: m.module_id === moduleId ? G : MU, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                {m.title}
              </div>
              {m.module_id === moduleId && m.lessons.map(l => {
                const done = isLessonDone(m.module_id, l.lesson_id)
                return (
                  <button
                    key={l.lesson_id}
                    onClick={() => {
                      navigate(`/learn/${classId}?module=${moduleId}&lesson=${l.lesson_id}`)
                      if (isMobile) setShowSidebar(false)
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: l.lesson_id === lessonId ? 'rgba(212,168,67,0.12)' : 'none', border: 'none', cursor: 'pointer', padding: '10px 12px', borderRadius: 10, textAlign: 'left', marginBottom: 2, transition: 'background 0.2s' }}
                  >
                    <div style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, background: done ? '#4ade80' : l.lesson_id === lessonId ? G : 'rgba(255,255,255,0.08)', color: done || l.lesson_id === lessonId ? '#06060c' : MU }}>
                      {done ? '✓' : l.lesson_id}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: l.lesson_id === lessonId ? 600 : 500, color: l.lesson_id === lessonId ? TX : MU, lineHeight: 1.4 }}>{l.title}</span>
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── Chat Area ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
        {/* Top bar */}
        <div style={{ padding: isMobile ? '0 12px' : '0 24px', height: 64, display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${BR}`, flexShrink: 0, background: 'rgba(13,13,26,0.8)', backdropFilter: 'blur(10px)', zIndex: 10 }}>
          {isMobile && (
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              style={{ background: 'none', border: 'none', color: TX, cursor: 'pointer', padding: 0, marginRight: 4 }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
            </button>
          )}
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: `linear-gradient(135deg, ${IN}, ${G})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🎓</div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <p style={{ fontSize: 14, fontWeight: 800, color: TX, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{classInfo.name.replace('BSc ', '')}</p>
            <p style={{ fontSize: 11, color: MU, margin: 0, fontWeight: 600 }}>{mod?.title}</p>
          </div>

          {/* ── Language Picker ── */}
          <LanguagePicker />
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px 14px 8px' : '24px 24px 16px' }} className="scrollbar-hide">
          {messages.length === 0 && !isLoading && (
            <div style={{ textAlign: 'center', paddingTop: isMobile ? 40 : 60 }}>
              <div style={{ fontSize: isMobile ? 32 : 40, marginBottom: 16 }}>🎓</div>
              <p style={{ color: MU, fontSize: 14, maxWidth: 260, margin: '0 auto', lineHeight: 1.5 }}>
                {t('university.session.intro', 'Your professor is ready. Ask a question or say hello to begin.')}
              </p>
            </div>
          )}

          {messages.map(msg => (
            <MessageBubble
              key={msg.id}
              content={msg.content}
              role={msg.role}
              isMobile={isMobile}
            />
          ))}

          {(isLoading || isStreaming) && (messages.length === 0 || messages[messages.length - 1]?.role === 'user') && (
            <div style={{ display: 'flex', marginBottom: 20, gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: `linear-gradient(135deg, ${IN}, ${G})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🎓</div>
              <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: '4px 18px 18px 18px' }}><TypingDots /></div>
            </div>
          )}

          <AnimatePresence>
            {error && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: '#f87171', fontWeight: 500 }}>{error}</span>
                <button onClick={clearError} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 16 }}>✕</button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Assessment — appears when session complete, based on essay_mode */}
          {sessionComplete && assessmentKind === 'short_answer' && (
            <ShortAnswerBox
              classId={classId}
              moduleId={moduleId}
              lessonId={lessonId}
              onDismiss={goNextLesson}
              onComplete={updateEnrollmentProgress}
            />
          )}
          {sessionComplete && assessmentKind === 'essay' && (
            <EssayDropbox
              classId={classId}
              moduleId={moduleId}
              className={classInfo.name}
            />
          )}

          {/* Lesson complete banner */}
          <AnimatePresence>
            {sessionComplete && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                style={{ background: 'rgba(212,168,67,0.1)', border: '1.5px solid rgba(212,168,67,0.4)', borderRadius: 20, padding: isMobile ? '20px' : '24px', marginBottom: 16, display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 16 }}>
                <div>
                  <p style={{ fontWeight: 800, fontSize: 17, color: G, marginBottom: 4, letterSpacing: '-0.3px' }}>
                    {quizResult ? (quizResult.passed ? '🎉 Lesson Passed!' : '📈 Keep Going!') : `🎉 ${t('university.session.lessonDone', 'Lesson Complete!')}`}
                  </p>
                  <p style={{ fontSize: 13, color: MU, fontWeight: 500 }}>
                    {quizResult && !quizResult.passed
                      ? "You've finished the lesson material. Review the feedback and try the quiz again when ready."
                      : isCapstoneLesson
                        ? t('university.session.lessonDoneCapstone', "You've reached the end of this degree. Head back to the overview.")
                        : isLastLesson
                          ? t('university.session.lessonDoneModule', 'Module complete! Continue to the next module when ready.')
                          : t('university.session.lessonDoneHint', 'Excellent progress. You can move to the next lesson or stay and discuss this one.')}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8, width: isMobile ? '100%' : 'auto' }}>
                  {quizResult && !quizResult.passed && (
                    <button onClick={() => sendMessage("I'd like to retake the quiz")}
                      style={{ background: 'rgba(255,255,255,0.05)', color: TX, border: `1px solid ${BR}`, borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                      Retake Quiz
                    </button>
                  )}
                  {assessmentKind === 'none' && (
                    <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={goNextLesson}
                      style={{ background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 12, padding: '12px 24px', fontSize: 14, fontWeight: 800, cursor: 'pointer', flex: 1 }}>
                      {isCapstoneLesson
                        ? t('university.session.backToDegreeBtn', 'Degree Overview') + ' →'
                        : isLastLesson
                          ? t('university.session.nextModule', 'Next Module') + ' →'
                          : t('university.session.next', 'Next Lesson') + ' →'}
                    </motion.button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Quiz loading indicator */}
          <AnimatePresence>
            {quizLoading && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.25)', borderRadius: 16, padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                  style={{ width: 20, height: 20, borderRadius: '50%', border: '2.5px solid rgba(14,165,233,0.2)', borderTopColor: IN, flexShrink: 0 }} />
                <span style={{ fontSize: 14, color: IN, fontWeight: 600 }}>{t('university.session.quizLoading', 'Generating your quiz…')}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Suggested action chips */}
          {suggestedChips.length > 0 && !quizOpen && !quizLoading && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              {suggestedChips.map(chip => (
                <motion.button
                  key={chip}
                  onClick={() => sendMessage(chip)}
                  whileHover={{ scale: 1.03, background: 'rgba(14,165,233,0.14)' }}
                  whileTap={{ scale: 0.97 }}
                  style={{
                    background: 'rgba(14,165,233,0.08)',
                    border: '1px solid rgba(14,165,233,0.25)',
                    borderRadius: 100, padding: '9px 18px',
                    fontSize: 13, color: '#7dd3fc',
                    cursor: 'pointer', fontWeight: 600,
                    transition: 'all 0.2s',
                  }}>
                  {chip}
                </motion.button>
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Quiz overlay */}
        {quiz && (
          <QuizInterface
            quiz={quiz}
            isOpen={quizOpen}
            onSubmit={submitQuiz}
            onDismiss={dismissQuiz}
            degreeName={classInfo.name}
          />
        )}

        {/* Input */}
        <div style={{ padding: isMobile ? '12px 14px 20px' : '16px 24px 24px', borderTop: `1px solid ${BR}`, flexShrink: 0, background: 'rgba(13,13,26,0.6)', backdropFilter: 'blur(20px)' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
              placeholder={isMobile ? t('university.session.placeholderMobile', 'Type your message...') : t('university.session.placeholder', 'Ask your professor anything… (Enter to send)')}
              rows={1} style={{ flex: 1, background: 'rgba(255,255,255,0.04)', border: `1.5px solid ${BR}`, borderRadius: 16, padding: isMobile ? '12px 14px' : '14px 18px', color: TX, fontSize: 15, outline: 'none', resize: 'none', fontFamily: "'Inter', sans-serif", lineHeight: 1.5, maxHeight: 120, transition: 'all 0.2s' }}
              onFocus={e => (e.target.style.borderColor = 'rgba(212,168,67,0.4)')}
              onBlur={e => (e.target.style.borderColor = BR)} />
            <motion.button whileHover={!isMobile ? { scale: 1.05 } : {}} whileTap={{ scale: 0.92 }} onClick={handleSend}
              disabled={!input.trim() || isLoading || isStreaming}
              style={{ width: 48, height: 48, borderRadius: 14, flexShrink: 0, background: input.trim() && !isLoading && !isStreaming ? `linear-gradient(135deg, ${G}, #b8912e)` : 'rgba(255,255,255,0.05)', border: 'none', cursor: input.trim() ? 'pointer' : 'not-allowed', color: input.trim() ? '#06060c' : MU, fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"></line><polyline points="5 12 12 5 19 12"></polyline></svg>
            </motion.button>
          </div>
          {!isMobile && (
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', textAlign: 'center', marginTop: 10, fontWeight: 500 }}>
              {t('university.session.footerHint', 'Shift+Enter for new line · Professor knows the context of this lesson')}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── SessionPage (outer) ─────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

export default function SessionPage() {
  const { classId } = useParams<{ classId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, isLoading: authLoading } = useAuth()

  const moduleId = parseInt(searchParams.get('module') || '1')
  const lessonId = parseInt(searchParams.get('lesson') || '1')

  const [classInfo, setClassInfo] = useState<ClassInfo | null>(null)
  const [loading, setLoading] = useState(true)

  const location = useLocation()

  useEffect(() => {
    if (!authLoading && !user) {
      const next = encodeURIComponent(location.pathname + location.search)
      navigate(`/signin?next=${next}`, { replace: true })
    }
  }, [user, authLoading, navigate, location])

  useEffect(() => {
    if (!user || !classId) return
    let cancelled = false
    supabase.from('classes').select('id, name, module_plan, essay_mode').eq('id', classId).single()
      .then(({ data }) => { if (!cancelled) { setClassInfo(data as ClassInfo); setLoading(false) } })
    return () => { cancelled = true }
  }, [user, classId])

  if (authLoading || (user && loading)) {
    return (
      <div style={{ background: M, height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
          style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid rgba(212,168,67,0.15)', borderTopColor: G }} />
      </div>
    )
  }

  if (!classInfo) {
    return (
      <div style={{ background: M, height: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: TX }}>
        <p style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Class not found</p>
        <button onClick={() => navigate('/prep')} style={{ background: G, color: '#06060c', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 700, cursor: 'pointer' }}>
          Back to Prep
        </button>
      </div>
    )
  }

  return <SessionInner classId={classId!} classInfo={classInfo} moduleId={moduleId} lessonId={lessonId} />
}
